/**
 * Typed free-floating header blocks (Brand, Menu, MenuToggle).
 *
 * Placement still lives in per-breakpoint boxes (anchor / x / y / w / h), the
 * same guide model as header-modules. Content props live on each block.
 *
 * Themes without header.blocks compile Brand + Menu from header.modules plus
 * brand.logo / header.nav* / fonts.nav (see blocksFromLegacyModules).
 */

import {
    breakpointKey,
    defaultHeaderModules,
    DEFAULT_LOGO_HEIGHT,
    DEFAULT_LOGO_HEIGHT_PHONE,
    DEFAULT_LOGO_WIDTH,
    DEFAULT_LOGO_WIDTH_PHONE,
    moduleBox,
} from './header-modules.js';
import { resolveFace } from '../library/resolve.js';
import { migrateCaptionStyle } from './caption.js';

export const HEADER_BLOCK_TYPES = {
    BRAND: 'brand',
    MENU: 'menu',
    MENU_TOGGLE: 'menuToggle',
};

/** Stable ids for the default Brand + Menu so legacy modules dual-write keeps working. */
export const DEFAULT_BRAND_ID = 'logo';
export const DEFAULT_MENU_ID = 'links';
export const DEFAULT_MENU_TOGGLE_ID = 'menuToggle';

const COLUMN_TYPES = new Set([
    HEADER_BLOCK_TYPES.BRAND,
    HEADER_BLOCK_TYPES.MENU,
    HEADER_BLOCK_TYPES.MENU_TOGGLE,
]);

let counter = 0;

/** @param {string} [type] */
export function newHeaderBlockId(type = 'block') {
    counter += 1;
    return `${type}-${Date.now().toString(36)}-${counter}`;
}

function cloneBox(box) {
    return box ? { ...box } : { h: 'auto' };
}

function cloneBoxes(entry) {
    if (!entry || typeof entry !== 'object') return {};
    const out = {};
    Object.keys(entry).forEach((bp) => {
        out[bp] = cloneBox(entry[bp]);
    });
    return out;
}

/** Default placement for a brand block (classic logo left). */
export function defaultBrandBoxes() {
    return cloneBoxes(defaultHeaderModules().logo);
}

/** Default placement for a menu block (classic links right; hidden on phone). */
export function defaultMenuBoxes() {
    return cloneBoxes(defaultHeaderModules().links);
}

/** Phone-visible hamburger; hidden on desktop and tablet. Height is the icon size (hug). */
export function defaultMenuToggleBoxes() {
    return {
        desktop: {
            anchor: 'end',
            x: '1.6em',
            y: 'center',
            w: 'auto',
            h: '2.5em',
            hidden: true,
            lockRatio: false,
            fit: 'hug',
        },
        tablet: {
            anchor: 'end',
            x: '1.6em',
            y: 'center',
            w: 'auto',
            h: '2.5em',
            hidden: true,
            lockRatio: false,
            fit: 'hug',
        },
        phone: {
            anchor: 'end',
            x: '1.25rem',
            y: 'center',
            w: 'auto',
            h: '2.5em',
            lockRatio: false,
            fit: 'hug',
        },
    };
}

/**
 * @param {string} type
 * @param {Object} [overrides]
 */
export function createHeaderBlock(type, overrides = {}) {
    const id = overrides.id || newHeaderBlockId(type);

    if (type === HEADER_BLOCK_TYPES.BRAND) {
        return {
            id,
            type: HEADER_BLOCK_TYPES.BRAND,
            props: {
                mode: 'mask',
                fileId: null,
                textHtml: '',
                text: '',
                faceId: null,
                hrefPageId: null,
                alt: '',
                /** 0–1; null keeps the long-standing 0.8 fade. */
                hoverOpacity: null,
                /** Text / tint on hover; null keeps the idle colour. */
                hoverColor: null,
                /** null = site default (brand underlines; menu does not). */
                hoverUnderline: null,
                ...(overrides.props || {}),
            },
            boxes: overrides.boxes || defaultBrandBoxes(),
        };
    }

    if (type === HEADER_BLOCK_TYPES.MENU) {
        return {
            id,
            type: HEADER_BLOCK_TYPES.MENU,
            props: {
                navSource: 'all-public',
                faceId: null,
                size: 1,
                tracking: 0.2,
                gap: 1,
                casing: 'uppercase',
                color: null,
                hoverColor: null,
                hoverUnderline: null,
                /** 0–1; null means no fade. */
                hoverOpacity: null,
                /** Font weight on hover; null keeps the link's own. */
                hoverWeight: null,
                /** 'italic' | 'normal'; null keeps the link's own. */
                hoverStyle: null,
                ...(overrides.props || {}),
            },
            boxes: overrides.boxes || defaultMenuBoxes(),
        };
    }

    if (type === HEADER_BLOCK_TYPES.MENU_TOGGLE) {
        return {
            id,
            type: HEADER_BLOCK_TYPES.MENU_TOGGLE,
            props: {
                label: 'Menu',
                /** Idle icon colour; null inherits page text colour. */
                color: null,
                hoverOpacity: null,
                hoverColor: null,
                /** Catalog glyph while the drawer is closed. Null keeps the three bars. */
                openGlyph: null,
                /** Catalog glyph while the drawer is open. Null keeps the three bars. */
                closeGlyph: null,
                /**
                 * Optional: pull Links shown / casing from a Menu block.
                 * Other drawer type props below still apply from this toggle.
                 */
                menuBlockId: null,
                /** Drawer link set; null → theme.menu.navSource. */
                drawerNavSource: null,
                drawerFaceId: null,
                /** em; null → classic 1.4. */
                drawerSize: null,
                drawerTracking: null,
                drawerGap: null,
                drawerCasing: null,
                /** left | center | right */
                drawerAlign: 'center',
                drawerColor: null,
                drawerHoverColor: null,
                drawerHoverOpacity: null,
                /** null keeps colors.sideMenuBackground. */
                drawerBackground: null,
                ...(overrides.props || {}),
            },
            boxes: overrides.boxes || defaultMenuToggleBoxes(),
        };
    }

    throw new Error(`Unknown header block type: ${type}`);
}

/**
 * Compile Brand + Menu from the fixed modules map and flat theme tokens.
 * @param {Object} theme
 * @returns {Object[]}
 */
export function blocksFromLegacyModules(theme) {
    const modules = theme?.header?.modules || defaultHeaderModules();
    const logo = theme?.brand?.logo || {};
    const header = theme?.header || {};
    const fonts = theme?.fonts || {};

    const brandMode = logo.mode || 'mask';
    const brandBoxes = cloneBoxes(modules.logo || defaultHeaderModules().logo);
    const brand = createHeaderBlock(HEADER_BLOCK_TYPES.BRAND, {
        id: DEFAULT_BRAND_ID,
        props: {
            mode: brandMode,
            fileId: logo.fileId ?? null,
            text: logo.text || '',
            textHtml: logo.text ? escapeHtml(logo.text) : '',
            faceId: fonts.wordmark || null,
            hrefPageId: logo.hrefPageId ?? null,
            alt: theme?.brand?.headerTitle || '',
        },
        boxes: brandMode === 'text' ? adaptBrandBoxesForMode(brandBoxes, 'text') : brandBoxes,
    });

    const menu = createHeaderBlock(HEADER_BLOCK_TYPES.MENU, {
        id: DEFAULT_MENU_ID,
        props: {
            navSource: header.navSource || 'all-public',
            faceId: fonts.nav || null,
            size: header.navSize ?? 1,
            tracking: header.navTracking ?? 0.2,
            gap: header.navGap ?? 1,
            casing: header.navCasing || 'uppercase',
            color: null,
        },
        boxes: cloneBoxes(modules.links || defaultHeaderModules().links),
    });

    // Phone hamburger replaces the floating control so Appearance can pick it.
    const toggle = createHeaderBlock(HEADER_BLOCK_TYPES.MENU_TOGGLE, {
        id: DEFAULT_MENU_TOGGLE_ID,
        props: {
            drawerNavSource: theme?.menu?.navSource || 'root-public',
            drawerCasing: theme?.menu?.navCasing || 'uppercase',
            drawerFaceId: fonts.nav || null,
        },
    });

    return [brand, menu, toggle];
}

/**
 * Resolved drawer (side menu) styles from the MenuToggle block + theme fallbacks.
 * @param {Object} theme
 * @param {Object} [library]
 * @returns {Object}
 */
export function resolveDrawerFromTheme(theme, library) {
    const blocks = theme?.header?.blocks || [];
    const toggle = blocks.find((block) => block?.type === HEADER_BLOCK_TYPES.MENU_TOGGLE);
    const props = toggle?.props || {};
    let navSource = props.drawerNavSource || theme?.menu?.navSource || 'root-public';
    let casing = props.drawerCasing || theme?.menu?.navCasing || 'uppercase';
    if (props.menuBlockId) {
        const menuBlock = blocks.find(
            (block) => block.id === props.menuBlockId && block.type === HEADER_BLOCK_TYPES.MENU,
        );
        if (menuBlock?.props) {
            navSource = menuBlock.props.navSource || navSource;
            casing = menuBlock.props.casing || casing;
        }
    }
    const size = props.drawerSize == null ? 1.4 : Number(props.drawerSize);
    const tracking = props.drawerTracking == null ? 0.25 : Number(props.drawerTracking);
    const gap = props.drawerGap == null ? 1 : Number(props.drawerGap);
    const lineHeight = props.drawerLineHeight == null ? null : Number(props.drawerLineHeight);
    const hoverOpacity = props.drawerHoverOpacity == null
        ? 0.7
        : Number(props.drawerHoverOpacity);
    const align = props.drawerAlign === 'left' || props.drawerAlign === 'right'
        ? props.drawerAlign
        : 'center';
    const faceId = props.drawerFaceId || theme?.fonts?.nav || null;
    // Resolve to a real family string. `--font-face-${id}` is invalid for
    // `face:…` ids; hover-preview of fonts.nav must not be required to stick.
    const fontFamily = faceId
        ? (resolveFace(faceId, library).css || 'var(--font-nav, "Sansumi", system-ui, sans-serif)')
        : 'var(--font-nav, "Sansumi", system-ui, sans-serif)';
    return {
        toggleId: toggle?.id || DEFAULT_MENU_TOGGLE_ID,
        hideFloatingToggle: Boolean(toggle),
        navSource,
        casing,
        faceId,
        size: Number.isFinite(size) ? size : 1.4,
        tracking: Number.isFinite(tracking) ? tracking : 0.25,
        lineHeight: Number.isFinite(lineHeight) ? lineHeight : null,
        weight: props.drawerWeight || null,
        style: props.drawerStyle === 'italic' ? 'italic' : null,
        gap: Number.isFinite(gap) ? gap : 1,
        align,
        color: props.drawerColor || null,
        hoverColor: props.drawerHoverColor || null,
        hoverOpacity: Number.isFinite(hoverOpacity) ? hoverOpacity : 0.7,
        background: props.drawerBackground || null,
        fontFamily,
    };
}

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Ensure theme.header.blocks exists. Does not mutate; returns a new theme when
 * blocks were synthesized. Already-blocked themes are returned as-is.
 * @param {Object} theme
 * @returns {Object}
 */
function brandNeedsTextHug(block) {
    if (block?.type !== HEADER_BLOCK_TYPES.BRAND) return false;
    if ((block.props?.mode || 'mask') !== 'text') return false;
    const desktop = block.boxes?.desktop;
    if (!desktop) return true;
    // Already hugging, or the author set a manual fixed width — leave alone.
    if (desktop.fit === 'hug' || desktop.w === 'auto' || desktop.fit === 'fixed') return false;
    // Legacy text brands still carrying the mask logo's fixed box (no fit flag).
    return true;
}

/** Normalize existing text-wordmark brands that still carry fixed SVG boxes. */
function normalizeTextBrandBoxes(theme) {
    const blocks = theme?.header?.blocks;
    if (!Array.isArray(blocks) || !blocks.some(brandNeedsTextHug)) return theme;
    const nextBlocks = blocks.map((block) => {
        if (!brandNeedsTextHug(block)) return block;
        return {
            ...block,
            boxes: adaptBrandBoxesForMode(block.boxes, 'text'),
        };
    });
    return {
        ...theme,
        header: {
            ...(theme.header || {}),
            blocks: nextBlocks,
            modules: modulesFromBlocks(nextBlocks, theme.header?.modules),
        },
    };
}

/** Themes saved before MenuToggle still get a pickable hamburger block. */
function ensureMenuToggleBlock(theme) {
    const blocks = theme?.header?.blocks;
    if (!Array.isArray(blocks) || blocks.length === 0) return theme;
    if (blocks.some((block) => block?.type === HEADER_BLOCK_TYPES.MENU_TOGGLE)) return theme;
    const id = blocks.some((block) => block?.id === DEFAULT_MENU_TOGGLE_ID)
        ? newHeaderBlockId('menuToggle')
        : DEFAULT_MENU_TOGGLE_ID;
    const nextBlocks = [...blocks, createHeaderBlock(HEADER_BLOCK_TYPES.MENU_TOGGLE, { id })];
    return {
        ...theme,
        header: {
            ...(theme.header || {}),
            blocks: nextBlocks,
            modules: modulesFromBlocks(nextBlocks, theme.header?.modules),
        },
    };
}

/**
 * Which pages each menu lists: 'all' (every page switched on for it), 'top'
 * (only top-level ones), or null for a header without a Menu block. The
 * header menu follows a page's Menu switch (isPublic), the side menu its Nav
 * switch (showInNav).
 * @param {Object} theme
 * @returns {{ header: 'all'|'top'|null, drawer: 'all'|'top' }}
 */
export function menuReach(theme) {
    const blocks = ensureHeaderBlocks(theme || {})?.header?.blocks || [];
    const menus = blocks.filter((block) => block?.type === HEADER_BLOCK_TYPES.MENU);
    const fallback = theme?.header?.navSource || 'all-public';
    let header = null;
    if (menus.length) {
        header = menus.some((block) => (block.props?.navSource || fallback) !== 'root-public') ? 'all' : 'top';
    }
    const drawer = resolveDrawerFromTheme(theme, null).navSource === 'all-public' ? 'all' : 'top';
    return { header, drawer };
}

export function ensureHeaderBlocks(input) {
    if (!input || typeof input !== 'object') return input;
    const theme = migrateCaptionStyle(input);
    const existing = theme.header?.blocks;
    if (Array.isArray(existing) && existing.length > 0) {
        return ensureMenuToggleBlock(normalizeTextBrandBoxes(theme));
    }
    const blocks = blocksFromLegacyModules(theme);
    return ensureMenuToggleBlock(normalizeTextBrandBoxes({
        ...theme,
        header: {
            ...(theme.header || {}),
            blocks,
            modules: modulesFromBlocks(blocks, theme.header?.modules),
        },
    }));
}

/**
 * Dual-write helpers: keep modules.logo|links in sync for viewers / repair.
 * @param {Object[]} blocks
 * @param {Object} [previousModules]
 */
export function modulesFromBlocks(blocks, previousModules = {}) {
    const modules = { ...(previousModules || {}) };
    const list = Array.isArray(blocks) ? blocks : [];
    list.forEach((block) => {
        if (!block?.id || !block.boxes) return;
        if (
            block.type === HEADER_BLOCK_TYPES.BRAND
            || block.type === HEADER_BLOCK_TYPES.MENU
            || block.type === HEADER_BLOCK_TYPES.MENU_TOGGLE
            || block.id === DEFAULT_BRAND_ID
            || block.id === DEFAULT_MENU_ID
        ) {
            modules[block.id] = cloneBoxes(block.boxes);
        }
    });
    // Preserve viewer chrome keys that are not header content blocks.
    Object.keys(previousModules || {}).forEach((key) => {
        if (!modules[key]) modules[key] = previousModules[key];
    });
    if (!modules.logo) modules.logo = defaultHeaderModules().logo;
    if (!modules.links) modules.links = defaultHeaderModules().links;
    return modules;
}

/**
 * @param {Object[]} blocks
 * @param {string} id
 * @param {string} [layer]
 */
export function headerBlockBox(blocks, id, layer = 'desktop') {
    const list = Array.isArray(blocks) ? blocks : [];
    const block = list.find((item) => item.id === id);
    if (block?.boxes) {
        return moduleBox({ [id]: block.boxes }, id, layer);
    }
    // Fall back to modules-shaped defaults for logo/links.
    return moduleBox(null, id, layer);
}

/**
 * Write a placement box onto a header block; dual-writes modules[id].
 * @param {Object} theme
 * @param {string} layer
 * @param {string} id
 * @param {Object} box
 */
export function writeHeaderBlockBox(theme, layer, id, box) {
    const bp = breakpointKey(layer);
    const ensured = ensureHeaderBlocks(theme);
    const blocks = (ensured.header?.blocks || []).map((block) => {
        if (block.id !== id) return block;
        return {
            ...block,
            boxes: {
                ...(block.boxes || {}),
                [bp]: box,
            },
        };
    });
    // Viewer / bar keys may not be in blocks — keep writing modules for those.
    const hasBlock = blocks.some((block) => block.id === id);
    let nextBlocks = blocks;
    if (!hasBlock && (id === 'bar' || String(id).startsWith('icon-') || String(id).startsWith('video-') || id === 'viewer-logo')) {
        nextBlocks = blocks;
    } else if (!hasBlock) {
        // Unknown id: still store on modules only (bridge bar height, icons).
        nextBlocks = blocks;
    }

    const modules = modulesFromBlocks(nextBlocks, ensured.header?.modules);
    modules[id] = { ...(modules[id] || {}), [bp]: box };

    return {
        ...ensured,
        header: {
            ...(ensured.header || {}),
            blocks: nextBlocks,
            modules,
        },
    };
}

/**
 * Patch block.props (and dual-write flat tokens for Brand/Menu defaults).
 * @param {Object} theme
 * @param {string} id
 * @param {Object} propsPatch
 */
/**
 * Text wordmarks must hug their glyphs so the selection outline matches the
 * painted letters. Fixed 160×3.36em boxes (SVG mask defaults) leave most of
 * the word overflowing and make drag/resize fight the visual size.
 */
export function adaptBrandBoxesForMode(boxes, mode) {
    const layers = ['desktop', 'phone', 'tablet'];
    const source = boxes && typeof boxes === 'object' ? boxes : {};
    const next = { ...source };
    layers.forEach((layer) => {
        if (!source[layer] && layer === 'tablet') return;
        const prev = source[layer] || (layer === 'phone'
            ? { anchor: 'start', x: '1.25rem', y: 'center', w: DEFAULT_LOGO_WIDTH_PHONE, h: DEFAULT_LOGO_HEIGHT_PHONE, lockRatio: true }
            : { anchor: 'start', x: '1.6em', y: 'center', w: DEFAULT_LOGO_WIDTH, h: DEFAULT_LOGO_HEIGHT, lockRatio: true });
        if (mode === 'text') {
            const fontSize = (prev.h && prev.h !== 'auto')
                ? prev.h
                : (layer === 'phone' ? DEFAULT_LOGO_HEIGHT_PHONE : DEFAULT_LOGO_HEIGHT);
            next[layer] = {
                ...prev,
                fit: 'hug',
                w: 'auto',
                h: fontSize,
                lockRatio: false,
            };
            return;
        }
        if (prev.fit === 'hug' || prev.w === 'auto') {
            next[layer] = {
                ...prev,
                fit: 'fixed',
                w: layer === 'phone' ? DEFAULT_LOGO_WIDTH_PHONE : DEFAULT_LOGO_WIDTH,
                h: (prev.h && prev.h !== 'auto')
                    ? prev.h
                    : (layer === 'phone' ? DEFAULT_LOGO_HEIGHT_PHONE : DEFAULT_LOGO_HEIGHT),
                lockRatio: true,
            };
        }
    });
    return next;
}

export function updateHeaderBlockProps(theme, id, propsPatch) {
    const ensured = ensureHeaderBlocks(theme);
    const blocks = (ensured.header?.blocks || []).map((block) => {
        if (block.id !== id) return block;
        const prevMode = block.props?.mode || 'mask';
        const nextProps = { ...(block.props || {}), ...(propsPatch || {}) };
        let nextBlock = { ...block, props: nextProps };
        if (
            block.type === HEADER_BLOCK_TYPES.BRAND
            && propsPatch
            && Object.prototype.hasOwnProperty.call(propsPatch, 'mode')
            && propsPatch.mode !== prevMode
        ) {
            nextBlock = {
                ...nextBlock,
                boxes: adaptBrandBoxesForMode(block.boxes, nextProps.mode || 'mask'),
            };
        }
        return nextBlock;
    });
    let next = {
        ...ensured,
        header: {
            ...(ensured.header || {}),
            blocks,
            modules: modulesFromBlocks(blocks, ensured.header?.modules),
        },
    };
    const block = blocks.find((item) => item.id === id);
    if (block?.type === HEADER_BLOCK_TYPES.BRAND && id === DEFAULT_BRAND_ID) {
        next = syncBrandTokensFromBlock(next, block, propsPatch);
    }
    if (block?.type === HEADER_BLOCK_TYPES.MENU && id === DEFAULT_MENU_ID) {
        next = syncMenuTokensFromBlock(next, block);
    }
    if (block?.type === HEADER_BLOCK_TYPES.MENU_TOGGLE) {
        next = syncDrawerTokensFromBlock(next, block);
    }
    return next;
}

function syncDrawerTokensFromBlock(theme, block) {
    const props = block.props || {};
    const menu = { ...(theme.menu || {}) };
    if (props.drawerNavSource) menu.navSource = props.drawerNavSource;
    if (props.drawerCasing) menu.navCasing = props.drawerCasing;
    if (props.drawerWeight != null) menu.drawerWeight = props.drawerWeight;
    else delete menu.drawerWeight;
    if (props.drawerStyle) menu.drawerStyle = props.drawerStyle;
    else delete menu.drawerStyle;
    if (props.drawerLineHeight != null) menu.drawerLineHeight = props.drawerLineHeight;
    else delete menu.drawerLineHeight;
    return { ...theme, menu };
}

function syncBrandTokensFromBlock(theme, block, propsPatch = {}) {
    const props = block.props || {};
    // Only a change of "Links to" may clear the theme link. Other brand edits
    // leave a null block prop alone so they do not wipe brand.logo.hrefPageId.
    const hrefPageId = Object.prototype.hasOwnProperty.call(propsPatch, 'hrefPageId')
        ? (propsPatch.hrefPageId || null)
        : (props.hrefPageId || theme.brand?.logo?.hrefPageId || null);
    const logo = {
        ...(theme.brand?.logo || {}),
        mode: props.mode || 'mask',
        fileId: props.fileId ?? null,
        text: props.text || stripTags(props.textHtml || ''),
        hrefPageId,
        // Absent until the brand inspector sets one, so other prop edits
        // do not wipe a filter written from the viewer logo control.
        ...(props.filter !== undefined ? { filter: props.filter } : {}),
    };
    if (props.weight != null) logo.weight = props.weight;
    else delete logo.weight;
    if (props.style) logo.fontStyle = props.style;
    else delete logo.fontStyle;
    if (props.tracking != null) logo.tracking = props.tracking;
    else delete logo.tracking;
    if (props.casing) logo.casing = props.casing;
    else delete logo.casing;
    const fonts = { ...(theme.fonts || {}) };
    if (props.faceId) fonts.wordmark = props.faceId;
    return {
        ...theme,
        fonts,
        brand: {
            ...(theme.brand || {}),
            logo,
            headerTitle: props.alt != null && props.alt !== ''
                ? props.alt
                : (theme.brand?.headerTitle || ''),
        },
    };
}

function syncMenuTokensFromBlock(theme, block) {
    const props = block.props || {};
    const header = {
        ...(theme.header || {}),
        navSource: props.navSource || 'all-public',
        navSize: props.size ?? 1,
        navTracking: props.tracking ?? 0.2,
        navGap: props.gap ?? 1,
        navCasing: props.casing || 'uppercase',
        ...(props.weight != null ? { navWeight: props.weight } : {}),
        ...(props.style ? { navStyle: props.style } : {}),
        ...(props.lineHeight != null ? { navLineHeight: props.lineHeight } : {}),
    };
    if (props.weight == null) delete header.navWeight;
    if (!props.style) delete header.navStyle;
    if (props.lineHeight == null) delete header.navLineHeight;
    const fonts = { ...(theme.fonts || {}) };
    if (props.faceId) fonts.nav = props.faceId;
    return { ...theme, header, fonts };
}

function stripTags(html) {
    return String(html || '').replace(/<[^>]*>/g, '').trim();
}

/**
 * @param {Object[]} blocks
 * @returns {string[]}
 */
export function listColumnHeaderBlockIds(blocks) {
    const list = Array.isArray(blocks) ? blocks : [];
    return list
        .filter((block) => block?.id && COLUMN_TYPES.has(block.type))
        .map((block) => block.id);
}

/**
 * @param {Object} block
 * @returns {boolean}
 */
export function headerBlockScalesFont(block) {
    return block?.type === HEADER_BLOCK_TYPES.BRAND;
}

/**
 * CSS class for a header block node (keeps logo/links classes for default ids).
 * @param {Object} block
 */
export function headerBlockClassName(block) {
    if (!block) return 'header-module';
    if (block.id === DEFAULT_BRAND_ID || block.type === HEADER_BLOCK_TYPES.BRAND) {
        return block.id === DEFAULT_BRAND_ID
            ? 'header-module header-module-logo header-module-brand'
            : 'header-module header-module-brand';
    }
    if (block.id === DEFAULT_MENU_ID || block.type === HEADER_BLOCK_TYPES.MENU) {
        return block.id === DEFAULT_MENU_ID
            ? 'header-module header-module-links header-module-menu'
            : 'header-module header-module-menu';
    }
    if (block.type === HEADER_BLOCK_TYPES.MENU_TOGGLE) {
        return 'header-module header-module-menu-toggle';
    }
    return 'header-module';
}

/**
 * Insert a new header block with default boxes.
 * @param {Object} theme
 * @param {string} type
 * @param {Object} [overrides]
 */
export function insertHeaderBlock(theme, type, overrides = {}) {
    const ensured = ensureHeaderBlocks(theme);
    const block = createHeaderBlock(type, overrides);
    const blocks = [...(ensured.header?.blocks || []), block];
    return {
        ...ensured,
        header: {
            ...(ensured.header || {}),
            blocks,
            modules: modulesFromBlocks(blocks, ensured.header?.modules),
        },
    };
}

/**
 * Remove a header block. Refuses to remove the last Brand.
 * @param {Object} theme
 * @param {string} id
 * @returns {{ theme: Object, error?: string }}
 */
export function removeHeaderBlock(theme, id) {
    const ensured = ensureHeaderBlocks(theme);
    const blocks = ensured.header?.blocks || [];
    const target = blocks.find((block) => block.id === id);
    if (!target) return { theme: ensured, error: 'Block not found' };
    if (target.type === HEADER_BLOCK_TYPES.BRAND) {
        const brandCount = blocks.filter((block) => block.type === HEADER_BLOCK_TYPES.BRAND).length;
        if (brandCount <= 1) {
            return { theme: ensured, error: 'Keep at least one brand block' };
        }
    }
    const next = blocks.filter((block) => block.id !== id);
    return {
        theme: {
            ...ensured,
            header: {
                ...(ensured.header || {}),
                blocks: next,
                modules: modulesFromBlocks(next, ensured.header?.modules),
            },
        },
    };
}

/**
 * Find a block by id on an ensured theme.
 * @param {Object} theme
 * @param {string} id
 */
export function findHeaderBlock(theme, id) {
    const ensured = ensureHeaderBlocks(theme);
    return (ensured.header?.blocks || []).find((block) => block.id === id) || null;
}

export default {
    HEADER_BLOCK_TYPES,
    DEFAULT_BRAND_ID,
    DEFAULT_MENU_ID,
    DEFAULT_MENU_TOGGLE_ID,
    newHeaderBlockId,
    createHeaderBlock,
    blocksFromLegacyModules,
    ensureHeaderBlocks,
    modulesFromBlocks,
    headerBlockBox,
    writeHeaderBlockBox,
    updateHeaderBlockProps,
    resolveDrawerFromTheme,
    listColumnHeaderBlockIds,
    headerBlockScalesFont,
    headerBlockClassName,
    insertHeaderBlock,
    removeHeaderBlock,
    findHeaderBlock,
};
